/**
 * Helpers for campaign start/end date handling.
 *
 * The date pickers are date-only, but the backend treats the window as
 * [startDate, endDate] inclusive at runtime. To make a campaign active for the
 * whole of its end day, we send the start at 00:00:00.000 and the end at
 * 23:59:59.999 (local time, then serialised to ISO/UTC).
 */

/**
 * @param {Date} date
 * @returns {string} ISO string at the start of the given day
 */
export const toStartOfDayIso = (date) => {
  const start = new Date(date);
  start.setHours(0, 0, 0, 0);
  return start.toISOString();
};

/**
 * @param {Date} date
 * @returns {string} ISO string at the end of the given day
 */
export const toEndOfDayIso = (date) => {
  const end = new Date(date);
  end.setHours(23, 59, 59, 999);
  return end.toISOString();
};

/**
 * Whether a campaign is effectively active right now. Prefers the backend's
 * computed `isActiveNow`; falls back to computing from status + start/end
 * window so the UI stays correct even before the backend change is deployed.
 *
 * @param {{ isActiveNow?: boolean, status?: string, startDate?: string|Date|null, endDate?: string|Date|null }} campaign
 * @returns {boolean}
 */
export const isCampaignEffectivelyActive = (campaign) => {
  if (!campaign) return false;
  if (typeof campaign.isActiveNow === "boolean") return campaign.isActiveNow;

  if (campaign.status !== "ACTIVE") return false;
  const now = new Date();
  if (campaign.startDate && new Date(campaign.startDate) > now) return false;
  if (campaign.endDate && new Date(campaign.endDate) < now) return false;
  return true;
};
